import {useCallback} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useLingui} from '@lingui/react/macro';
import {useToast} from 'react-native-toast-notifications';
import {useModal} from 'extensions/viewport/useModal';
import {useNavigate} from 'extensions/navigation';
import {Prompt} from 'interface/base/Prompt';
import {selectors} from 'store/slices/files';
import {createFolder, loadFolder} from 'utils/mediafire';

export function useModalCreateFolder(id: string) {
  const {t} = useLingui();
  const sort = useSelector(selectors.getSort);
  const filter = useSelector(selectors.getFilter);
  const modal = useModal();
  const toast = useToast();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const submit = useCallback(async (value: string) => {
    if (value) {
      const res = await createFolder(id, value);
      if (res && res['name']) {
        loadFolder(navigate, dispatch, id, false, true, undefined, sort, filter);
        toast.show(t`Created the folder “${res['name']}”`); 
      } else {
        toast.show(res?.message ?? t`Failed to create folder`, {type: 'danger'});
      }
    }
    modal.exit();
  }, [id, sort, filter]);

  const open = useCallback(() => {
    modal.open(
      <Prompt
        title={t`Create Folder`}
        text={t`Give this folder a name`}
        options={{
          type: 'plain-text',
          placeholder: t`Folder name`,
        }}
        buttons={[
          {
            mode: 'primary',
            text: t`Ok`.toUpperCase(),
            onPress: submit,
          },
          {
            mode: 'cancel',
            text: t`Cancel`.toUpperCase(),
            onPress: modal.exit,
          },
        ]}
      />
    );
  }, [id]);

  return {open, exit: modal.exit};
}
